import { Inject, Injectable, Optional } from "@angular/core";

import { combineLatest, Observable, of } from "rxjs";
import { distinctUntilChanged, map, shareReplay } from "rxjs/operators";

import { MenuItem } from "@dffedb/ui";
import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "@e-forsyning/common/app-shell/app-shell-layout.token";
import { AktuelBrugerObserver, Bruger, Rolle } from "@e-forsyning/common/bruger";
import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { UlaesteDokumenterService } from "@e-forsyning/common/edh";
import { DocumentNotificationService } from "@e-forsyning/common/push-notification";
import { isTeknikerContext } from "@e-forsyning/common/util";
import { FeatureFlagService } from "@e-forsyning/features/feature-flag";

import { EforsyningMenuerObserver } from "./eforsyning-menuer.observer";

@Injectable({ providedIn: "root" })
export class EforsyningMenuForSidebarObserver {
    private readonly _valueChanges: Observable<MenuItem[]>;

    constructor(
        private readonly eforsyningMenuerObserver: EforsyningMenuerObserver,
        private readonly installationCredentialsRepository: AppserverInstallationCredentialsRepository,
        private readonly aktuelBrugerObserver: AktuelBrugerObserver,
        private readonly ulaesteDokumenterService: UlaesteDokumenterService,
        private readonly documentNotificationService: DocumentNotificationService,
        @Optional() @Inject(APP_SHELL_LAYOUT_OPTIONS) private readonly appShellLayoutOptions: AppShellLayoutOptions | null,
        featureFlagService: FeatureFlagService
    ) {
        const isTeknikerMode = isTeknikerContext();
        const eforsyningNodeId$ = this.installationCredentialsRepository.valueChanges.pipe(
            map((c) => (c && c.forsyningNodeId) || 0),
            distinctUntilChanged()
        );
        const bruger$ = this.aktuelBrugerObserver.valueChanges;
        const menu$ = this.eforsyningMenuerObserver.valueChanges;
        const ulaesteDokumenter$ = isTeknikerMode ? of(0) : this.ulaesteDokumenterService.hentAntalUlaesteDokumenter();

        this._valueChanges = combineLatest([menu$, eforsyningNodeId$, bruger$, ulaesteDokumenter$, featureFlagService.isFeatureEnabledObservable()]).pipe(
            map(([menu, eforsyningNodeId, bruger, ulaesteDokumenter, shutdownEnabled]) =>
                this.bygMenu(menu, eforsyningNodeId, bruger, ulaesteDokumenter, shutdownEnabled)
            ),
            shareReplay(1)
        );
    }

    public get valueChanges(): Observable<MenuItem[]> {
        return this._valueChanges;
    }

    private bygMenu(menu: MenuItem, eforsyningNodeId: number, bruger: Bruger, ulaesteDokumenter: number, shutdownEnabled: boolean): MenuItem[] {
        return eforsyningNodeId && bruger ? this.bygLoggetIndMenu(menu, eforsyningNodeId, bruger, ulaesteDokumenter, shutdownEnabled) : this.bygLoggetUdMenu(menu);
    }

    private bygLoggetUdMenu(menu: MenuItem): MenuItem[] {
        return menu.children.length === 1
            ? [{ title: "Log ind", href: menu.children[0].href, adgangsstyret: false }]
            : menu.children.map((c) => ({ title: c.title, href: c.href, adgangsstyret: c.adgangsstyret }));
    }

    private bygLoggetIndMenu(menu: MenuItem, eforsyningNodeId: number, bruger: Bruger, ulaesteDokumenter: number, shutdownEnabled: boolean): MenuItem[] {
        const eforsyningMenu = menu.children.find((i) => i.id === eforsyningNodeId);
        const showFofMenu = Rolle.erFofBruger(bruger.rolle) && !shutdownEnabled;
        const fofMenuItems = showFofMenu ? this.tilføjBadgesTilMenuItems(this.findFofMenuItems(eforsyningMenu), ulaesteDokumenter) : [];
        const forbrugerMenuItems =
            this.appShellLayoutOptions?.hideForbrugerMenu === true ? [] : this.genererForbrugerMenuItems(eforsyningMenu, ulaesteDokumenter, showFofMenu);
        const loginOgLogoutMenuItems = this.genererLoginOgLogoutMenuItems(eforsyningMenu);
        return [...fofMenuItems, ...forbrugerMenuItems, ...loginOgLogoutMenuItems];
    }

    private genererForbrugerMenuItems(eforsyning: MenuItem, ulaesteDokumenter: number, showFofMenu: boolean): MenuItem[] {
        const children = this.fjernLoginOgLogoutMenuItems(this.findForbrugerMenuItems(eforsyning));
        const childrenMedBadges = this.tilføjBadgesTilMenuItems(children, ulaesteDokumenter);
        return showFofMenu && childrenMedBadges.length
            ? [{ title: "Forbruger", icon: "menu-forbruger", adgangsstyret: false, children: childrenMedBadges }]
            : childrenMedBadges;
    }

    private genererLoginOgLogoutMenuItems(eforsyning: MenuItem): MenuItem[] {
        return this.findLoginOgLogoutMenuItems(this.findForbrugerMenuItems(eforsyning));
    }

    private findFofMenuItems(eforsyning: MenuItem): MenuItem[] {
        const item = eforsyning && eforsyning.children.find((c) => c.name === "eforsyning-fof-bruger");
        return (item && item.children) || [];
    }

    private findForbrugerMenuItems(eforsyning: MenuItem): MenuItem[] {
        const item = eforsyning && eforsyning.children.find((c) => c.name === "eforsyning-forbruger");
        return (item && item.children) || [];
    }

    private fjernLoginOgLogoutMenuItems(menuItems: MenuItem[]): MenuItem[] {
        const loginOgLogout = this.findLoginOgLogoutMenuItems(menuItems);
        return menuItems.filter((i) => !loginOgLogout.includes(i));
    }

    private findLoginOgLogoutMenuItems(menuItems: MenuItem[]): MenuItem[] {
        return menuItems.filter((i) => i.icon === "menu-login" || i.icon === "menu-logout");
    }

    private tilføjBadgesTilMenuItems(menuItems: MenuItem[], ulaesteDokumenter: number): MenuItem[] {
        return menuItems.map((item) => {
            // Check for "mine-dokumenter" in route, href, or name
            const isMineDokumenter =
                item.route === "/mine-dokumenter" ||
                item.href === "/mine-dokumenter" ||
                item.href === "#/mine-dokumenter" ||
                item.name === "mine-dokumenter" ||
                item.title === "Mine dokumenter";

            if (isMineDokumenter && ulaesteDokumenter > 0) {
                return { ...item, badge: ulaesteDokumenter };
            }

            // Check for notification center
            const isNotificationCenter =
                item.route === "/notifikationer" || item.href === "/notifikationer" || item.name === "notifikationer" || item.title === "Notifikationer";

            if (isNotificationCenter) {
                // Her kunne vi tilføje badge baseret på ulæste notifikationer
                // For nu returnerer vi bare item uden badge
                return item;
            }

            return item;
        });
    }
}
